import { Component, OnInit } from '@angular/core';
import { PagedResponse } from '@shared/models/paged-response';
import { PageEvent } from '@shared/models/page-event';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ApiService } from '@shared/apis';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import Utils from '@shared/utils/utils';
import { User } from '@shared/models/user';
import { FormControl } from '@angular/forms';
import { SpinnerService } from '@shared/services/spinner.service';
import { UserEditComponent } from '@routes/users/user-edit/user-edit.component';
import { AuthService } from '@core/auth/auth.service';
import Configs from '@shared/constants/configs';
import Roles from '@shared/constants/roles';
import { Company } from '@shared/enums/company';
import Role from '@shared/constants/role';
import { Options } from '@shared/models/options';
import { ParamsBuilder } from '@shared/models/params-builder';
import DateUtils from '@shared/utils/date-utils';
import { FileService } from '@shared/services/file.service';
import { environment } from '@env/environment';
import { Constants } from '@shared/constants/constants';
import Swal from 'sweetalert2';


@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss']
})
export class UserComponent implements OnInit {

  readonly configs = Configs;
  readonly color = Configs.badgeColor;
  readonly constants = Constants;
  readonly backend = environment.api.backend;
  realPage: number;

  isAdmin = false;
  isAdminOnly = false;

  showDetail: boolean;
  pagedResponse: PagedResponse;

  query: any;
  q: any;
  searchText = new FormControl(null);

  userData!: User[];
  selectedData!: User;
  companyCodes: any[] = [];


  bsModalRef: BsModalRef;
  isCreatable = false;
  isAdminViewerRole: boolean;
  isDealerSupervisor = false;
  isHq = false;
  company;
  companies = Company;

  constructor(
    private modalService: BsModalService,
    private spinnerService: SpinnerService,
    private authService: AuthService,
    private apiService: ApiService,
    private fileService: FileService,
  ) {
    this.isAdmin = this.authService.hasType(Roles.admin);
    this.isAdminOnly = this.authService.hasType(Roles.adminOnly);
    this.isHq = this.authService.isHq();
    this.isDealerSupervisor = this.authService.hasRoleInCompany(Role.secaDealerSuperVisor);
    this.company = this.authService.getCurrentCompany();
    this.isAdminViewerRole = this.authService.checkHasAdminViewerRole();
  }

  ngOnInit(): void {
    this.getOrganizationName();
    this.showList();

    this.isCreatable = ([Company.SeaIm, Company.Seca].includes(this.company) && this.isDealerSupervisor) || this.isAdmin;
    this.searchText.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(text => {
        this.q = text;
        this.showList();
      });
  }

  getOrganizationName() {
    this.companyCodes = this.authService.getCompanyCodes();
  }

  onPageEvent(pageEvent: PageEvent) {
    this.showList(pageEvent);
  }

  showList(pageEvent?: PageEvent) {
    this.spinnerService.start();
    this.apiService.user.getUsers(pageEvent, this.q, this.query)
      .pipe(finalize(() => this.spinnerService.stop()))
      .subscribe(res => {
        const company = this.authService.getCurrentCompany();
        this.pagedResponse = res;
        this.userData = res.docs.map((m) => {
          m.roles = m.roles?.find(f => f.company === company)?.groups?.map(g => this.constants.role[g])?.join(', ');
          m.customerType = m.userType;
          return m;
        });
      }, err => Utils.showError(err));
  }

  modalOpen() {
    const initialState = {
      title: 'User Create', saveButtonLabel: 'Create',
    };

    this.bsModalRef = this.modalService.show(UserEditComponent, { initialState, class: 'modal-lg' });
    this.bsModalRef.content.onClose.subscribe(result => {
      if (result) {
        this.showList();
      }
    });
  }

  selectRow(user) {
    if (user === this.selectedData) {
      return;
    }
    this.showDetailInfo(user);
  }

  showDetailInfo(user) {
    const { username } = user;

    this.spinnerService.start();
    this.apiService.user.getUser(username)
      .pipe(finalize(() => this.spinnerService.stop()))
      .subscribe(res => {
        this.selectedData = res;
        this.selectedData.organizationName = user.organizationName;
        this.showDetail = true;
      }, err => Utils.showError(err));
  }

  reload(data) {
    if (data === 'detailReload') {
      this.showDetailInfo(this.selectedData);
    } else {
      this.showList();
      this.showDetailInfo(data);
    }
  };

  options() {
    const opt: Options = {
      q: this.q,
      query: this.query,
    };
    return opt;
  }

  exportResult(pageEvent?: PageEvent) {
    const opt: Options = this.options();

    if (!pageEvent) {
      pageEvent = new PageEvent();
      pageEvent.limit = this.configs.pagination.sizeOptions[0];
      this.realPage = pageEvent.page;
    }

    const builder = new ParamsBuilder();
    builder.pageEvent = pageEvent;

    if (opt?.q) {
      builder.q = opt.q;
    }
    if (opt?.query) {
      builder.query = opt.query;
    }
    if (opt?.filter) {
      builder.filter = opt.filter;
    }

    if (this.pagedResponse.totalDocs >= 1000) {
      builder.export = 'email';
      this.spinnerService.start('table');
      this.apiService.user.exportUsers(builder.build())
        .pipe(finalize(() => this.spinnerService.stop('table')))
        .subscribe(res => {
          Swal.fire({
            title: 'User List was exported',
            text: 'User list would be sent to your email address.',
            icon: 'success',
            allowOutsideClick: false,
          }).then((result) => {
          });
        }, err => Utils.showError(err));
    } else {
      builder.export = 'download';
      const isPost = true;
      const fileName = `USER-${ DateUtils.toHqMomentFormat(new Date(), 'YYYYMMDDHHmmssSSS') }`;
      this.fileService.downloadFile(this.backend.concat('/user/export'), fileName, builder.build(), isPost);
    }
  }
}
